import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  AtendimentoResumo,
  buscarRegiaoOficina,
  FiltrosOficina,
  RespostaRegiao,
} from '../../../api/dashboardOficinaApi';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import { notify } from '../../../shared/feedback/notify';
import { corDaSerie, formatarNumero, TINTA_GRAFICO } from '../../../shared/ui/chart-palette';
import DetalhePlacasModal from '../../../shared/ui/DetalhePlacasModal';
import DetalheTabelaModal from '../../../shared/ui/DetalheTabelaModal';
import {
  AreaEstado,
  CardGrafico,
  EIXO_LINHA,
  EIXO_TICK,
  ESTILO_TOOLTIP,
  Kpi,
} from '../../../shared/ui/GraficoBase';
import TabelaPaginada, { ColunaTabela } from '../../../shared/ui/TabelaPaginada';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/feedback/erro-de-tela.css';
import '../../../shared/ui/dashboard-base.css';

export const helpSectionsRegiao: ComoFuncionaSecao[] = [
  {
    titulo: 'Que endereço é este',
    conteudo: (
      <>
        <p>
          O endereço de <strong>residência do associado</strong> dono do veículo atendido, vindo do
          cadastro do SGA. É a área de captação real da oficina: de onde vêm os carros que ela
          atende.
        </p>
        <p>
          Não é o local onde o evento aconteceu — esse dado depende de uma coleta que ainda está
          sendo construída, e vai entrar aqui como uma segunda leitura.
        </p>
      </>
    ),
  },
  {
    titulo: 'Volume e escala, lado a lado',
    conteudo: (
      <>
        <p>
          O ranking mostra de onde vêm <strong>mais veículos</strong>. Ao lado, a coluna{' '}
          <strong>Frota protegida</strong> diz quantos veículos a associação tem naquele bairro —
          é ela que dá a escala: sete atendimentos num bairro de dez mil veículos e sete num de
          duzentos são coisas diferentes.
        </p>
        <p>
          Já existiu aqui uma coluna que dividia uma pela outra ("atendimentos por mil veículos").
          Ela saiu em 27/08/2026: em bairros com pouquíssimos veículos protegidos, um único
          atendimento produzia números enormes e sem sentido prático.
        </p>
      </>
    ),
  },
  {
    titulo: 'Tempo na oficina, e por que não se chama permanência',
    conteudo: (
      <>
        <p>
          Quanto tempo, em média, os veículos daquele bairro ficam na oficina. Diferenças grandes
          entre regiões podem indicar dificuldade de o associado buscar o carro — o veículo fica
          pronto e continua ocupando o pátio.
        </p>
        <p>
          ⚠️ Inclui os veículos que <strong>ainda estão</strong> na oficina, contados até hoje. É
          diferente da <strong>Permanência média</strong> da aba Visão Geral, que só considera
          atendimentos encerrados (da entrada até a entrega). Por isso os dois números não batem, e
          por isso o nome aqui é outro.
        </p>
      </>
    ),
  },
  {
    titulo: 'Só entram os veículos com cadastro',
    conteudo: (
      <p>
        Sem cadastro no SGA não há endereço, então esses veículos — em geral <strong>terceiros</strong>,
        que a associação não cadastra — ficam fora desta aba. A quantidade aparece escrita abaixo do
        título.
      </p>
    ),
  },
];

interface Props {
  filtros: FiltrosOficina;
  onJanela?: (janela: { dataInicio: string; dataFim: string }) => void;
  sidebarRef?: React.RefObject<HTMLElement>;
}

const RegiaoTab: React.FC<Props> = ({ filtros, onJanela, sidebarRef }) => {
  const [dados, setDados] = useState<RespostaRegiao | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  /**
   * Detalhamento de LUGARES: bairros ou cidades com a contagem de veículos.
   *
   * ⚠️ Os indicadores desta aba falam de região, não de veículo — abrir uma lista de placas
   * respondia a pergunta errada. O veículo continua alcançável: clicar numa região abre a lista
   * dela, num segundo passo.
   */
  const [lugares, setLugares] = useState<{
    titulo: string;
    tipo: 'bairro' | 'cidade';
    apoio: string;
  } | null>(null);
  const [detalhe, setDetalhe] = useState<{
    titulo: string;
    placas: string[];
    colunas: Array<ColunaTabela<AtendimentoResumo>>;
  } | null>(null);

  const carregar = useCallback(
    async (f: FiltrosOficina) => {
      setCarregando(true);
      try {
        const resposta = await buscarRegiaoOficina(f);
        /*
         * ⚠️ Resposta sem o formato esperado é tratada como FALHA, não como dado.
         *
         * Acontece de verdade quando o servidor em execução é anterior à versão da tela (campo
         * renomeado, por exemplo). Sem esta guarda a leitura estoura em `undefined.algo` e
         * derruba a página com erro de runtime — tela branca, que é pior que número faltando.
         */
        if (!resposta || typeof resposta.porBairro === 'undefined') {
          throw new Error('Resposta do servidor em formato inesperado');
        }

        /*
         * ⚠️ Resposta sem o formato esperado é tratada como FALHA, não como dado.
         *
         * Acontece de verdade quando o servidor em execução é anterior à versão da tela (campo
         * renomeado, por exemplo). Sem esta guarda a leitura estoura em `undefined.algo` e
         * derruba a página com erro de runtime — tela branca, que é pior que número faltando.
         */
        if (!resposta || typeof resposta.porBairro === 'undefined') {
          throw new Error('Resposta do servidor em formato inesperado');
        }

        setDados(resposta);
        setErro(null);
        onJanela?.({ dataInicio: resposta.janela.dataInicio, dataFim: resposta.janela.dataFim });
      } catch (e) {
        setDados(null);
        setErro(
          'Não foi possível carregar a distribuição por região. Enquanto este aviso estiver na tela, não há número para ler aqui — recarregue a página; se persistir, acione a TI.',
        );
        notify.errorFrom(
          e,
          'Não foi possível carregar a distribuição por região. Recarregue a página; se persistir, acione a TI.',
          { toastId: 'dashboard-oficina-regiao-falhou' },
        );
      } finally {
        setCarregando(false);
      }
    },
    [onJanela],
  );

  useEffect(() => {
    carregar(filtros);
  }, [filtros, carregar]);

  const respondeu = dados !== null;
  const primeiraCarga = carregando && !respondeu;
  const textoVazio = respondeu
    ? 'Nenhum veículo deste recorte tem endereço no cadastro do SGA.'
    : 'Não foi possível carregar — veja o aviso acima.';

  // `?? []` cria array novo a cada render; sem o memo, todo useMemo que depende dele recalcula.
  const porBairro = useMemo(() => dados?.porBairro ?? [], [dados]);
  const cobertura = dados?.cobertura;
  const abrir = (
    titulo: string,
    placas: string[],
    colunas: Array<ColunaTabela<AtendimentoResumo>>,
  ) => setDetalhe({ titulo, placas, colunas });

  /**
   * Colunas dos modais desta aba: onde o dono mora e há quanto tempo o carro está aqui.
   *
   * ⚠️ São as informações do recorte que o modal abriu — bairro e cidade —, não colunas
   * genéricas. Quem clica num bairro quer conferir de quem são aqueles carros.
   */
  const COL: Array<ColunaTabela<AtendimentoResumo>> = [
    { titulo: 'Bairro', largura: 200, texto: (i) => i.bairro ?? '—' },
    { titulo: 'Cidade', largura: 180, texto: (i) => i.cidade ?? '—' },
    { titulo: 'Dias na oficina', largura: 140, texto: (i) => (i.naOficina ? i.diasNaOficina + ' dias' : 'Entregue') },
  ];

  /**
   * O bairro de onde vêm mais veículos.
   *
   * ⚠️ Volume puro, e isso é uma DECISÃO de 27/08/2026: a versão anterior mostrava
   * "atendimentos por 1.000 veículos protegidos" para corrigir o viés de bairro grande. A
   * correção estava certa na intenção e errada na prática — bairro com 2 veículos protegidos e
   * 1 atendimento aparecia com "500 por mil" no topo, e a métrica era ilegível para quem opera.
   * Volume é honesto sobre o que mede, e a coluna "Frota protegida" dá a escala ao lado.
   */
  const bairroDestaque = useMemo(
    () =>
      porBairro.length > 0
        ? porBairro.reduce((maior, atual) => (atual.quantidade > maior.quantidade ? atual : maior))
        : null,
    [porBairro],
  );

  /** Cidades com contagem — a resposta já as traz agregadas. */
  const cidades = useMemo(() => dados?.porCidade ?? [], [dados]);

  /** Bairros com maior tempo médio na oficina — leitura operacional, não estatística. */
  const topPorTempo = useMemo(
    () =>
      [...porBairro]
        .filter((b) => b.tempoNaOficinaMediaDias > 0)
        .sort((a, b) => b.tempoNaOficinaMediaDias - a.tempoNaOficinaMediaDias)
        .slice(0, 10),
    [porBairro],
  );

  return (
    <div className={carregando && respondeu ? 'do-atualizando' : undefined}>
      {erro && (
        <div className="erro-de-tela" role="alert">
          {erro}
        </div>
      )}

      <div className="do-aba-cabecalho">
        <div className="do-aba-titulo">
          Região
          <button type="button" className="help-circle-btn" title="Como Funciona" onClick={() => setIsHelpOpen(true)}>
            ?
          </button>
        </div>
      </div>

      {respondeu && cobertura && (
        <p className="do-nota-janela">
          Baseado nos{' '}
          <strong>
            {formatarNumero(cobertura.localizados)} de {formatarNumero(cobertura.atendimentos)}
          </strong>{' '}
          veículos com endereço no cadastro do SGA. Os demais — em geral terceiros, que a associação
          não cadastra — não têm endereço para posicionar.
        </p>
      )}

      <div className="do-kpis">
        <Kpi
          rotulo="Bairros atendidos"
          valor={respondeu ? formatarNumero(porBairro.length) : '-'}
          apoio="Com ao menos um veículo no recorte"
          carregando={primeiraCarga}
          onDetalhar={
            porBairro.length > 0
              ? () =>
                  setLugares({
                    titulo: 'Bairros atendidos',
                    tipo: 'bairro',
                    apoio:
                      'Bairros de onde vêm os veículos atendidos, do maior volume para o menor. Clique num bairro para ver os veículos dele.',
                  })
              : undefined
          }
          detalheRotulo="Ver os bairros atendidos"
        />
        <Kpi
          rotulo="Cidades atendidas"
          valor={respondeu ? formatarNumero(dados.porCidade.length) : '-'}
          apoio="Residência do associado"
          carregando={primeiraCarga}
          onDetalhar={
            cidades.length > 0
              ? () =>
                  setLugares({
                    titulo: 'Cidades atendidas',
                    tipo: 'cidade',
                    apoio:
                      'Cidades de onde vêm os veículos atendidos. Clique numa cidade para ver os veículos dela.',
                  })
              : undefined
          }
          detalheRotulo="Ver as cidades atendidas"
        />
        <Kpi
          rotulo="Bairro com mais veículos"
          valor={bairroDestaque ? bairroDestaque.rotulo : respondeu ? 'Sem bairro' : '-'}
          apoio={
            bairroDestaque
              ? `${formatarNumero(bairroDestaque.quantidade)} veículos atendidos`
              : 'De onde vêm mais veículos'
          }
          carregando={primeiraCarga}
          onDetalhar={
            porBairro.length > 0
              ? () =>
                  setLugares({
                    titulo: 'Bairros atendidos',
                    tipo: 'bairro',
                    apoio:
                      'Bairros de onde vêm os veículos atendidos, do maior volume para o menor. Clique num bairro para ver os veículos dele.',
                  })
              : undefined
          }
          detalheRotulo="Ver os bairros atendidos"
        />
      </div>

      <div className="do-grid">
        <CardGrafico
          titulo="Atendimentos por bairro"
          descricao="Volume bruto: de onde vêm mais carros. Clique numa barra para ver as placas."
        >
          <div className="do-plot-alto">
            {!respondeu || porBairro.length === 0 ? (
              <AreaEstado carregando={primeiraCarga} mensagem={textoVazio} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={porBairro.slice(0, 10)} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <CartesianGrid stroke={TINTA_GRAFICO.grade} horizontal={false} />
                  <XAxis type="number" tick={EIXO_TICK} axisLine={EIXO_LINHA} tickLine={false} allowDecimals={false} />
                  <YAxis type="category" dataKey="rotulo" tick={EIXO_TICK} axisLine={EIXO_LINHA} tickLine={false} width={170} />
                  <Tooltip contentStyle={ESTILO_TOOLTIP} formatter={(v: number) => [formatarNumero(v), 'Atendimentos']} />
                  <Bar
                    dataKey="quantidade"
                    radius={[0, 4, 4, 0]}
                    cursor="pointer"
                    onClick={(evento: any) =>
                      evento?.payload && abrir(`Bairro: ${evento.payload.rotulo}`, evento.payload.placas, COL)
                    }
                  >
                    {porBairro.slice(0, 10).map((b) => (
                      <Cell key={b.rotulo} fill={corDaSerie(0)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardGrafico>

        <CardGrafico
          titulo="Tempo médio na oficina por bairro"
          descricao="Há quantos dias, em média, os veículos de cada bairro ficam na oficina. Inclui os que ainda estão aqui. Clique numa barra para ver os veículos."
        >
          <div className="do-plot-alto">
            {!respondeu || topPorTempo.length === 0 ? (
              <AreaEstado
                carregando={primeiraCarga}
                mensagem={respondeu ? 'Nenhum bairro com tempo apurado neste recorte.' : textoVazio}
              />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topPorTempo} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <CartesianGrid stroke={TINTA_GRAFICO.grade} horizontal={false} />
                  <XAxis type="number" tick={EIXO_TICK} axisLine={EIXO_LINHA} tickLine={false} />
                  <YAxis type="category" dataKey="rotulo" tick={EIXO_TICK} axisLine={EIXO_LINHA} tickLine={false} width={170} />
                  <Tooltip
                    contentStyle={ESTILO_TOOLTIP}
                    formatter={(v: number) => [`${v} dias`, 'Tempo médio']}
                  />
                  <Bar
                    dataKey="tempoNaOficinaMediaDias"
                    radius={[0, 4, 4, 0]}
                    cursor="pointer"
                    onClick={(evento: any) =>
                      evento?.payload && abrir(`Bairro: ${evento.payload.rotulo}`, evento.payload.placas, COL)
                    }
                  >
                    {topPorTempo.map((b) => (
                      <Cell key={b.rotulo} fill={corDaSerie(5)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardGrafico>
      </div>

      <div className="do-grid-largo">
        <CardGrafico
          titulo="Detalhe por bairro"
          descricao="Volume, frota protegida da região, uso proporcional e tempo médio na oficina. Clique numa linha para ver as placas."
        >
          {!respondeu || porBairro.length === 0 ? (
            <div className="do-plot">
              <AreaEstado carregando={primeiraCarga} mensagem={textoVazio} />
            </div>
          ) : (
            <TabelaPaginada
              itens={porBairro}
              chave={(linha) => linha.rotulo}
              vazio="Nenhum bairro neste recorte."
              onClickLinha={(linha) => abrir(`Bairro: ${linha.rotulo}`, linha.placas, COL)}
              colunas={[
                { titulo: 'Bairro', largura: 200, texto: (l) => l.rotulo },
                { titulo: 'Cidade', largura: 180, texto: (l) => l.cidade },
                { titulo: 'Atendimentos', largura: 120, texto: (l) => formatarNumero(l.quantidade) },
                {
                  titulo: 'Frota protegida',
                  largura: 140,
                  texto: (l) => (l.frotaProtegida ? formatarNumero(l.frotaProtegida) : '—'),
                },
                {
                  titulo: 'Tempo na oficina (média)',
                  largura: 180,
                  texto: (l) => `${l.tempoNaOficinaMediaDias} dias`,
                },
              ]}
            />
          )}
        </CardGrafico>
      </div>

      {/*
        ⚠️ Ao escolher uma região, o modal de lugares FECHA e o de veículos abre no lugar dele.
        Modal filho é renderizado fora do `ref` do hospedeiro, então o handler de "clicou fora"
        do pai trataria qualquer clique no filho como externo e se fecharia no primeiro clique —
        fechar antes elimina o caso por construção (mesma receita do ConviteAcoesModal).
      */}
      {lugares && dados && (
        <DetalheTabelaModal
          titulo={lugares.titulo}
          apoio={lugares.apoio}
          itens={lugares.tipo === 'bairro' ? (lugares.titulo.startsWith('Uso') ? topPorTempo : porBairro) : cidades}
          chave={(l: any) => l.rotulo}
          vazio="Nenhuma região neste recorte."
          onClickLinha={(l: any) => {
            setLugares(null);
            abrir(`${lugares.tipo === 'cidade' ? 'Cidade' : 'Bairro'}: ${l.rotulo}`, l.placas, COL);
          }}
          colunas={
            lugares.tipo === 'cidade'
              ? [
                  { titulo: 'Cidade', largura: 240, texto: (l: any) => l.rotulo },
                  { titulo: 'Veículos', largura: 130, texto: (l: any) => formatarNumero(l.quantidade) },
                ]
              : [
                  { titulo: 'Bairro', largura: 220, texto: (l: any) => l.rotulo },
                  { titulo: 'Cidade', largura: 190, texto: (l: any) => l.cidade },
                  { titulo: 'Veículos', largura: 120, texto: (l: any) => formatarNumero(l.quantidade) },
                  {
                    titulo: 'Tempo na oficina (média)',
                    largura: 190,
                    texto: (l: any) => `${l.tempoNaOficinaMediaDias} dias`,
                  },
                ]
          }
          onFechar={() => setLugares(null)}
          sidebarRef={sidebarRef}
        />
      )}

      {detalhe && dados && (
        <DetalhePlacasModal
          titulo={detalhe.titulo}
          placas={detalhe.placas}
          colunas={detalhe.colunas}
          atendimentos={dados.atendimentos}
          onFechar={() => setDetalhe(null)}
          sidebarRef={sidebarRef}
        />
      )}

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Região"
        secoes={helpSectionsRegiao}
      />
    </div>
  );
};

export default RegiaoTab;
